import { useMutation } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  AdyenPaymentClient,
  AdyenPaymentResultInput,
  AdyenSessionRequest,
  SubmitStoredPaymentRequest,
} from 'src/WebApiController';

// TODO: Restore feature flag check after testing
// import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
// import { Feature } from 'src/WebApiController';

export function useCreateAdyenPaymentSession() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  // TODO: Restore feature flag check after testing
  // const hasAdyenFeature = useUserHasFeature(Feature.AdyenOnlinePayments);

  return useMutation({
    mutationFn: async (request: AdyenSessionRequest) => {
      return await new AdyenPaymentClient(
        activeAccountWebClientConfig
      ).createPaymentSession(request);
    },
    onError: (error: ErrorTypes) => {
      trackError('AdyenPaymentClient.createPaymentSession', error);
      showErrorDialog('AdyenPaymentClient.createPaymentSession', error);
    },
  });
}

export function useSubmitAdyenPaymentResult() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();

  return useMutation({
    mutationFn: async ({
      salesGroupId,
      input,
    }: {
      salesGroupId: string | null | undefined;
      input: AdyenPaymentResultInput;
    }) => {
      return await new AdyenPaymentClient(
        activeAccountWebClientConfig
      ).submitPaymentResult(salesGroupId, input);
    },
    onError: (error: ErrorTypes, { salesGroupId }) => {
      trackError('AdyenPaymentClient.submitPaymentResult', error, {
        salesGroupId,
      });
      showErrorDialog('AdyenPaymentClient.submitPaymentResult', error, {
        trackErrorData: { salesGroupId },
      });
    },
  });
}

export function useSubmitPaymentWithStoredMethod() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();

  return useMutation({
    mutationFn: async (request: SubmitStoredPaymentRequest) => {
      return await new AdyenPaymentClient(
        activeAccountWebClientConfig
      ).submitPaymentWithStoredMethod(request);
    },
    onError: (error: ErrorTypes) => {
      trackError('AdyenPaymentClient.submitPaymentWithStoredMethod', error);
      showErrorDialog(
        'AdyenPaymentClient.submitPaymentWithStoredMethod',
        error
      );
    },
  });
}

/** Refunds the full Adyen charge for a whole sales group. */
export function useRefundGroupPayments() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();

  return useMutation({
    mutationFn: async (salesGroupId: string) => {
      return await new AdyenPaymentClient(
        activeAccountWebClientConfig
      ).refundGroupPayments(salesGroupId);
    },
    onError: (error: ErrorTypes) => {
      trackError('AdyenPaymentClient.refundGroupPayments', error);
      showErrorDialog('AdyenPaymentClient.refundGroupPayments', error);
    },
  });
}
